import { useCallback, useState } from 'react'
import type { ComponentType } from '@/types/schemas/workspace'
import { PANEL_CATEGORY, PANEL_CATEGORY_ORDER } from './add-panel-menu-constants'

// Collapsed category names persist across menu opens (bounded: ≤ the handful of
// PANEL_CATEGORY_ORDER entries, so it can't balloon the localStorage quota).
const COLLAPSED_KEY = 'ett:add-panel-collapsed-categories'

/** Shared with the Panel Hub so both surfaces fold the same categories. */
export function useCollapsedCategories() {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY)
      return new Set(raw ? (JSON.parse(raw) as string[]) : [])
    } catch {
      return new Set()
    }
  })
  const toggle = useCallback((cat: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]))
      } catch {
        /* private-mode / quota: the collapse just will not persist */
      }
      return next
    })
  }, [])
  return { collapsed, toggle }
}

/** Group types by category in PANEL_CATEGORY_ORDER; uncategorized types fall to
 *  a trailing null-category group (rendered without a header). */
export function groupByCategory(
  types: ComponentType[],
): { category: string | null; types: ComponentType[] }[] {
  const byCat = new Map<string | null, ComponentType[]>()
  for (const t of types) {
    const cat = PANEL_CATEGORY[t] ?? null
    const bucket = byCat.get(cat)
    if (bucket) bucket.push(t)
    else byCat.set(cat, [t])
  }
  const ordered: { category: string | null; types: ComponentType[] }[] = []
  for (const cat of PANEL_CATEGORY_ORDER) {
    const ts = byCat.get(cat)
    if (ts) {
      ordered.push({ category: cat, types: ts })
      byCat.delete(cat)
    }
  }
  // Any remaining (uncategorized) go last, unlabeled.
  for (const [cat, ts] of byCat) ordered.push({ category: cat, types: ts })
  return ordered
}
